<template>
	<div
		class="fixed inset-0 flex items-center justify-center"
		style="z-index: 10005; background-color: rgba(0, 0, 0, 0.5)"
	>
		<div class="relative">
			<button
				class="absolute top-0 right-0 text-gray-500 hover:text-gray-700 focus:outline-none pr-2"
				@click="$emit('close')"
				aria-label="Close"
			>
				&times;
			</button>
			<div class="flex flex-col sm:flex-row gap-6 bg-white rounded-lg p-6 shadow-xl">
				<div class="flex border-1 border-gray-200 rounded-md">
					<div class="max-w-sm min_width p-2 bg-gray-100 rounded-md">
						<div class="overflow-auto pr-2" style="max-height: 285px;">
							<div
								v-for="item in order.items"
								:key="item.id"
								class="flex place-items-start justify-items-center justify-between gap-2"
							>
								<span>{{ item.quantity }}x {{ item.itemName }}</span>
								<span class="whitespace-nowrap">{{
									$filters.formatCurrency(item.itemPrice * item.quantity, order.currency)
								}}</span>
							</div>
						</div>
						<app-line class="my-2" />
						<div class="flex justify-between text-gray-700 font-bold">
							<span>Total:</span>
							<span>{{ $filters.formatCurrency(order.total, order.currency) }}</span>
						</div>
					</div>
				</div>
				<div>
					<div v-if="showQuestion" class="place-items-center">
						<div class="flex place-items-center gap-1 text-xl font-bold">
							{{ $t('can_you_make_it') }}
						</div>
						<div class="flex place-items-center gap-1 text-xl font-bold">
							<span class="">{{ $t('in') }}</span>
							<span class="px-1 bg-header text-white rounded-md"
								>{{ order.orderExtraData.suggestedCookingTime }} min</span
							>
							<span>?</span>
						</div>
						<div class="w-24 h-24">
							<Vue3Lottie :animationData="chefCooking" :loop="true" :scale="1" :autoPlay="true" />
						</div>
						<div class="flex flex-col gap-2 w-full">
							<app-button
								@click="acceptOrder(order.orderExtraData.suggestedCookingTime)"
								:loading="loading"
								class="btn btn-success font-semibold transparent rounded-md"
							>
								{{ $t('yes_I_agree') }}
							</app-button>
							<app-button @click="showQuestion = false" class="btn btn-success font-semibold rounded-md">
								{{ $t('no_change_time') }}
							</app-button>
						</div>
					</div>
					<div v-else class="text-center w-full">
						<div class="text-2xl font-bold mb-4">
							{{ $t('select_cooking_time') }}
						</div>
						<div class="grid grid-cols-3 justify-center gap-2 mb-2 w-full">
							<app-button
								v-for="time in order.orderExtraData.cookingTimeOptions"
								:key="time"
								class="w-full p-2 font-medium hover:bg-success hover:text-white border-1 border-gray-300"
								:class="
									`w-full p-2 font-medium hover:bg-success hover:text-white border-1 border-gray-300
								${isSelected(time.value)}`
								"
								@click="updateCurrentTime(time.value)"
								:disabled="!time.isActive"
							>
								{{ time.value }} <span class="text-13">min</span>
							</app-button>
							<app-button
								@click="showCustomPicker = !showCustomPicker"
								class="w-full p-2 font-medium hover:bg-success hover:text-white border-1 border-gray-300"
								:class="isCustomSelected"
							>
								{{ $t('other') }}
							</app-button>
						</div>
						<div v-if="showCustomPicker" class="custom-picker-container">
							<div class="custom-picker">
								<div
									v-for="option in customTimeOptions"
									:key="option.value"
									:class="`custom-picker-item ${option.value === customTime ? 'selected' : ''}`"
									@click="selectCustomTime(option.value)"
								>
									{{ option.name }}
								</div>
							</div>
						</div>
						<app-button
							@click="confirmCustomTime"
							:loading="loading"
							:disabled="!customTime"
							class="btn btn-success w-full mt-4"
						>
							{{ $t('confirm') }}
						</app-button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import chefCooking from '@/assets/animations/chefcooking.json';
	import AppLine from '@/components/app/AppLine.vue';
	import { Vue3Lottie } from 'vue3-lottie';
	import { statuses } from '@/constants/mapOrderStatuses';
	import OrderService from '@/services/OrderService';
	import moment from 'moment';

	export default {
		name: 'CookingTimeModal',
		components: { Vue3Lottie, AppLine },
		emits: ['updateLocalOrder', 'close', 'rerenderTimer'],
		props: {
			order: {
				type: Object,
				default: () => ({}),
			},
		},
		data() {
			return {
				chefCooking,
				showQuestion: false,
				showCustomPicker: false,
				customTimeOptions: Array.from(
					{
						length:
							this.order.orderExtraData.maxSuggestedCookingTime -
							this.order.orderExtraData.minSuggestedCookingTime +
							1,
					},
					(_, i) => ({
						value: this.order.orderExtraData.minSuggestedCookingTime + i,
						name: `${this.order.orderExtraData.minSuggestedCookingTime + i} min`,
					})
				),
				customTime: null,
				loading: false,
			};
		},
		computed: {
			isCustomSelected() {
				return this.showCustomPicker ? 'bg-success text-white' : '';
			},
		},
		methods: {
			acceptOrder(cookingTime) {
				this.loading = true;
				this.$store.dispatch('setLoadingOrderId', this.order.orderID);
				if (this.order.status === statuses.rejected) {
					this.$emit('close');
				} else {
					OrderService.acceptOrder(this.order.orderID, cookingTime)
						.then(() => {
							this.$emit('updateLocalOrder', {
								orderId: this.order.orderID,
								props: {
									status: statuses.accepted,
									totalCookingTime: cookingTime,
									lastDateAccepted: moment().format('YYYY-MM-DD HH:mm:ss'),
								},
							});
							this.$emit('rerenderTimer', this.order.orderID);
							this.$emit('close');
						})
						.catch((error) => {
							this.$notificationService.error(error);
							this.$emit('close');
						})
						.finally(() => {
							this.loading = false;
							this.$store.dispatch('setLoadingOrderId', null);
						});
				}
			},
			isSelected(time) {
				return this.customTime === time ? 'bg-success text-white' : '';
			},
			updateCurrentTime(value) {
				this.customTime = value;
				this.showCustomPicker = false;
			},
			selectCustomTime(value) {
				this.customTime = value;
			},
			confirmCustomTime() {
				this.acceptOrder(this.customTime);
				this.$emit('close');
			},
		},
		mounted() {
			if (this.order.orderExtraData.suggestedCookingTime) {
				this.selectCustomTime(this.order.orderExtraData.suggestedCookingTime);
			}
		},
	};
</script>

<style scoped>
	.custom-picker-container {
		max-height: 150px;
		overflow-y: auto;
		border: 1px solid #ccc;
		border-radius: 4px;
		margin-top: 10px;
	}
	.custom-picker {
		display: flex;
		flex-direction: column;
		align-items: center;
	}
	.custom-picker-item {
		padding: 10px;
		text-align: center;
		cursor: pointer;
		width: 100%;
	}
	.custom-picker-item.selected {
		background-color: #28a745;
		color: white;
		font-weight: bold;
	}
	.min_width {
		min-width: 16em;
	}
</style>
